import { updateApi, useApi } from "@/api";
import { session } from "@/api/session";
import { ContentType, TCountry, TCurrency, TLanguage } from "@/api/v1_0";
import { config } from "@/helpers";
import { RootStore } from "@/store/root";
import { makeAutoObservable } from "mobx";

export class AppStore {
  private _rootStore: RootStore;

  public loading = true;
  public submitting = false;
  public logged = false;
  public offline = false;

  private _country?: TCountry = undefined;
  private _currency?: TCurrency = undefined;
  private _language?: TLanguage = undefined;

  private _showDebugMenu?: boolean = undefined;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeAutoObservable(this);
  }

  public set country(value: TCountry) {
    this._country = value;
  }

  public get hasCountry(): boolean {
    return !!this._country;
  }

  public get country(): TCountry {
    return this._country || TCountry.RUS;
  }

  public set currency(value: TCurrency) {
    this._currency = value;
  }

  public get hasCurrency(): boolean {
    return !!this._currency;
  }

  public get currency(): TCurrency {
    return this._currency || TCurrency.RUB;
  }

  public set language(value: TLanguage) {
    this._language = value;
  }

  public get hasLanguage(): boolean {
    return !!this._language;
  }

  public get language(): TLanguage {
    return this._language || TLanguage.Ru;
  }

  public set showDebugMenu(value: boolean) {
    this._showDebugMenu = value;
  }

  public get showDebugMenu(): boolean {
    return this._showDebugMenu || false;
  }

  public clear = async (): Promise<void> => {
    this._rootStore.logging.track("app.clear");

    this._rootStore.parameters.clear();
    session.clear();
    this._rootStore.auth.signOut();
  };

  public toggleDebugMenu = () => {
    this._showDebugMenu = !this._showDebugMenu;
  };

  public healthCheck = async (): Promise<void> => {
    const api = useApi();

    try {
      const { data: result } = await api.v10.appHttpApiV10ControllerHealthCheck(
        {
          platform: "web",
          version: config.version || "0.1.0",
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      updateApi(result.apiHost);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
    }
  };

  public launch = async (): Promise<void> => {
    await this._rootStore.logging.start();
    this._rootStore.logging.track("app.launch");
    await Promise.all([this._rootStore.parameters.load(), session.load()]);

    try {
      if (!session.hasAccessToken() || !session.hasRefreshToken()) {
        // Router.push("/signin");
      } else {
        try {
          await this._rootStore.profile.read();
          this.logged = true;

          await this.load();

          // Router.push("/dashboard");
        } catch (_err) {
          this._rootStore.auth.signOut();

          // Router.push("/signin");
        }
      }
    } finally {
      this.loading = false;
    }
  };

  public load = async (): Promise<void> => {
    this._rootStore.logging.track("app.load");

    this.loading = true;

    try {
      await Promise.all([
        this._rootStore.parameters.load(),
        this._rootStore.wallet.loadRates(),
        this._rootStore.wallet.load(),
        this._rootStore.dashboard.load(),
        this._rootStore.operations.load(),
      ]);

      this._rootStore.popup.load();
    } finally {
      this.loading = false;
    }
  };

  public goPro = () => {
    this._rootStore.popup.pro = false;
    // Router.push("/account/pro");
  };
}
